"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeRevisionImpact = computeRevisionImpact;
exports.mergeRevisionImpacts = mergeRevisionImpacts;
exports.isActionAllowed = isActionAllowed;
function toDateOnly(value) {
    if (!value)
        return null;
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime()))
        return null;
    return d.toISOString().slice(0, 10);
}
function flattenMilestones(milestones) {
    const map = new Map();
    for (const m of milestones ?? []) {
        if (m.id) {
            map.set(m.id, {
                id: m.id,
                budgetAmount: Number(m.budgetAmount ?? 0),
                startDate: toDateOnly(m.startDate),
                endDate: toDateOnly(m.endDate),
                name: m.name ?? "",
                description: m.description ?? "",
            });
        }
        for (const sub of m.subMilestones ?? []) {
            if (!sub.id)
                continue;
            map.set(sub.id, {
                id: sub.id,
                budgetAmount: Number(sub.budgetAmount ?? 0),
                startDate: toDateOnly(sub.startDate),
                endDate: toDateOnly(sub.endDate),
                name: sub.name ?? "",
                description: sub.description ?? "",
                projectInvoiceId: sub.projectInvoiceId ?? null,
            });
        }
    }
    return map;
}
/** Diff a pending ProjectRevision's metadata against the pre-revision snapshot to compute what actually changed. */
function computeRevisionImpact(metadata) {
    const originalById = flattenMilestones(metadata?.originalMilestones);
    const proposedById = flattenMilestones(metadata?.milestonesRevision);
    const explicitDeletedIds = new Set((metadata?.deletedMilestones ?? []).map((d) => d.milestoneId));
    const implicitDeletedIds = [...originalById.keys()].filter((id) => !proposedById.has(id));
    const deletedIds = new Set([...explicitDeletedIds, ...implicitDeletedIds]);
    const items = {};
    for (const [id, proposed] of proposedById) {
        const original = originalById.get(id);
        if (!original) {
            items[id] = { itemId: id, impact: "structuralChange", isNew: true, isDeleted: false, invoiceReassigned: false };
            continue;
        }
        // Only sub-milestones carry projectInvoiceId; top-level milestones have undefined on both sides
        // (flattenMilestones never sets the field) — their invoice assignment is tracked via invoicesRevision.milestoneIds.
        const invoiceReassigned = proposed.projectInvoiceId !== undefined && original.projectInvoiceId !== undefined && proposed.projectInvoiceId !== original.projectInvoiceId;
        const budgetChanged = proposed.budgetAmount !== original.budgetAmount;
        const scheduleChanged = proposed.startDate !== original.startDate || proposed.endDate !== original.endDate;
        const cosmeticChanged = proposed.name !== original.name || proposed.description !== original.description;
        let impact;
        if (invoiceReassigned)
            impact = "structuralChange";
        else if (budgetChanged)
            impact = "budgetChanged";
        else if (scheduleChanged)
            impact = "scheduleOnly";
        else if (cosmeticChanged)
            impact = "cosmeticOnly";
        else
            impact = "untouched";
        items[id] = { itemId: id, impact, isNew: false, isDeleted: false, invoiceReassigned };
    }
    for (const id of deletedIds) {
        if (items[id])
            continue;
        items[id] = { itemId: id, impact: "structuralChange", isNew: false, isDeleted: true, invoiceReassigned: false };
    }
    const originalInvoicesById = new Map((metadata?.originalInvoices ?? []).map((inv) => [inv.id, inv]));
    const invoices = {};
    for (const revInvoice of metadata?.invoicesRevision ?? []) {
        if (!revInvoice.id)
            continue;
        const underlyingIds = new Set([...(revInvoice.milestoneIds ?? []), ...(revInvoice.voSubMilestoneIds ?? [])]);
        const originalInvoice = originalInvoicesById.get(revInvoice.id);
        const removedFromInvoiceIds = originalInvoice
            ? [...(originalInvoice.projectMilestones ?? []).map((pm) => pm.id), ...(originalInvoice.voSubMilestoneIds ?? [])].filter((id) => !underlyingIds.has(id))
            : [];
        let invoiceTouched = !originalInvoice;
        let budgetDelta = false;
        let structuralDelta = removedFromInvoiceIds.length > 0;
        let newItemsAdded = false;
        for (const itemId of underlyingIds) {
            const itemImpact = items[itemId];
            if (!itemImpact)
                continue;
            if (itemImpact.impact !== "untouched")
                invoiceTouched = true;
            if (itemImpact.impact === "budgetChanged" || itemImpact.isNew || itemImpact.isDeleted)
                budgetDelta = true;
            if (itemImpact.impact === "structuralChange" || itemImpact.invoiceReassigned)
                structuralDelta = true;
            if (itemImpact.isNew)
                newItemsAdded = true;
        }
        // A deleted item is absent from underlyingIds, so it never reaches the loop above —
        // structuralDelta/budgetDelta already account for it via removedFromInvoiceIds.
        invoiceTouched = invoiceTouched || structuralDelta || budgetDelta;
        invoices[revInvoice.id] = { invoiceId: revInvoice.id, invoiceTouched, budgetDelta, structuralDelta, newItemsAdded };
    }
    // Catch invoices that lost all their items to deletion and were dropped from invoicesRevision
    // entirely (e.g. the FE removes "orphaned" invoices instead of submitting them) — without this,
    // such an invoice has no entry in `invoices` and isActionAllowed defaults to allowing actions on it.
    const itemIdToOriginalInvoiceId = new Map();
    for (const inv of metadata?.originalInvoices ?? []) {
        for (const pm of inv.projectMilestones ?? []) {
            itemIdToOriginalInvoiceId.set(pm.id, inv.id);
        }
        for (const voId of inv.voSubMilestoneIds ?? []) {
            itemIdToOriginalInvoiceId.set(voId, inv.id);
        }
    }
    for (const itemId of deletedIds) {
        const invoiceId = itemIdToOriginalInvoiceId.get(itemId);
        if (!invoiceId || invoices[invoiceId])
            continue;
        invoices[invoiceId] = { invoiceId, invoiceTouched: true, budgetDelta: false, structuralDelta: true, newItemsAdded: false };
    }
    const hasAnyItemChange = Object.values(items).some((i) => i.impact !== "untouched");
    const hasAnyInvoiceChange = Object.values(invoices).some((i) => i.invoiceTouched);
    const projectMetadataOnly = !hasAnyItemChange && deletedIds.size === 0 && !hasAnyInvoiceChange;
    return {
        hasPendingRevision: true,
        projectMetadataOnly,
        items,
        invoices,
    };
}
/** OR-merge multiple pending revisions' impacts — an item/invoice is blocked if ANY pending revision would block it. */
function mergeRevisionImpacts(impacts) {
    if (impacts.length === 0)
        return null;
    if (impacts.length === 1)
        return impacts[0];
    const items = {};
    const invoices = {};
    const impactSeverity = { untouched: 0, cosmeticOnly: 1, scheduleOnly: 2, budgetChanged: 3, structuralChange: 4 };
    for (const impact of impacts) {
        for (const [id, item] of Object.entries(impact.items)) {
            const existing = items[id];
            if (!existing || impactSeverity[item.impact] > impactSeverity[existing.impact]) {
                items[id] = item;
            }
            else if (existing) {
                items[id] = { ...existing, isNew: existing.isNew || item.isNew, isDeleted: existing.isDeleted || item.isDeleted, invoiceReassigned: existing.invoiceReassigned || item.invoiceReassigned };
            }
        }
        for (const [id, inv] of Object.entries(impact.invoices)) {
            const existing = invoices[id];
            invoices[id] = existing
                ? {
                    invoiceId: id,
                    invoiceTouched: existing.invoiceTouched || inv.invoiceTouched,
                    budgetDelta: existing.budgetDelta || inv.budgetDelta,
                    structuralDelta: existing.structuralDelta || inv.structuralDelta,
                    newItemsAdded: existing.newItemsAdded || inv.newItemsAdded,
                }
                : inv;
        }
    }
    const projectMetadataOnly = impacts.every((i) => i.projectMetadataOnly);
    return { hasPendingRevision: true, projectMetadataOnly, items, invoices };
}
const INVOICE_BLOCKED_REASONS = {
    requestPayment: "Cannot request payment while a revision that changes this invoice is under review. Please wait for the client to accept or decline the revision.",
    requestDrawdown: "Cannot request a drawdown while a revision that changes this invoice is under review. Please wait for the client to accept or decline the revision.",
    markAsPaid: "Cannot mark this invoice as paid while a revision that changes it is under review. Please wait for the client to accept or decline the revision.",
};
/** Decide whether an action on a milestone/sub-milestone/invoice is allowed given a project's pending revision impact. */
function isActionAllowed(action, impact, ctx) {
    if (!impact || impact.projectMetadataOnly) {
        return { allowed: true };
    }
    if (action === "statusTransition") {
        const itemImpact = ctx.itemId ? impact.items[ctx.itemId] : undefined;
        if (!itemImpact || itemImpact.impact === "untouched" || itemImpact.impact === "cosmeticOnly") {
            return { allowed: true };
        }
        if (itemImpact.impact === "scheduleOnly") {
            if (ctx.isFunded && ctx.fromStatus === "in_progress") {
                return { allowed: false, reason: "Cannot change status while a revision that changes this item's schedule is under review and payment is in escrow." };
            }
            return { allowed: true };
        }
        return { allowed: false, reason: "Cannot change status while a revision that changes this item's budget or structure is under review." };
    }
    const invoiceImpact = ctx.invoiceId ? impact.invoices[ctx.invoiceId] : undefined;
    if (!invoiceImpact || (!invoiceImpact.budgetDelta && !invoiceImpact.structuralDelta && !invoiceImpact.newItemsAdded)) {
        return { allowed: true };
    }
    return { allowed: false, reason: INVOICE_BLOCKED_REASONS[action] };
}
